import { useEffect, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { isAdminUser, getUserEmail } from '../lib/admin';
import { handleFirestoreError, OperationType } from '../utils/errorHandling';

export function useFirebaseSync(user: any, setters: any) {
  const autoLinkAttemptedRef = useRef<string | null>(null);
  
  // Use a ref to keep track of the latest setters without triggering re-renders
  const settersRef = useRef(setters);
  useEffect(() => {
    settersRef.current = setters;
  }, [setters]);

  useEffect(() => {
    let userUnsubscribe: (() => void) | null = null;
    let currentUser = user;
    let isAdminEmail = isAdminUser(currentUser);

    if (currentUser) {
        // Load user settings from Firestore with real-time updates
        const docRef = doc(db, 'users', currentUser.uid);

        // Garante que todo usuário logado tenha o cadastro em users/{uid}:
        // - recria o documento se ele não existir (ex.: excluído pelo admin no Painel GOD,
        //   mas a conta de login continuou existindo);
        // - migra um cadastro antigo com o mesmo e-mail, quando houver;
        // - preenche e-mail/uid que estejam faltando (ex.: contas Google salvas sem e-mail).
        if (!isAdminEmail && autoLinkAttemptedRef.current !== currentUser.uid) {
          autoLinkAttemptedRef.current = currentUser.uid;
          const emailLower = getUserEmail(currentUser);
          const checkAndLink = async () => {
            try {
              const { getDoc, collection, query, where, getDocs, setDoc } = await import('firebase/firestore');
              const snap = await getDoc(docRef);
              if (!snap.exists()) {
                // 1) Tenta migrar um cadastro antigo com o mesmo e-mail
                let migrated = false;
                if (emailLower) {
                  try {
                    const querySnap = await getDocs(query(collection(db, 'users'), where('email', '==', emailLower)));
                    if (!querySnap.empty) {
                      const existingData = querySnap.docs[0].data();
                      console.log("[App] Migrating/linking existing user document to new UID", currentUser.uid);
                      await setDoc(docRef, { ...existingData, uid: currentUser.uid, email: emailLower }, { merge: true });
                      migrated = true;
                    }
                  } catch (migrateErr) {
                    console.warn("[App] Migração por e-mail indisponível; criando cadastro novo.", migrateErr);
                  }
                }
                // 2) Senão, recria o cadastro do zero (fica pendente de aprovação)
                if (!migrated) {
                  console.log("[App] Creating user document for UID", currentUser.uid);
                  await setDoc(docRef, {
                    uid: currentUser.uid,
                    email: emailLower,
                    name: currentUser.displayName || "",
                    createdAt: new Date().toISOString(),
                    subscription_status: 'pending',
                  }, { merge: true });
                }
              } else {
                const existingData = snap.data();
                if (!existingData || (!existingData.email && emailLower) || !existingData.uid) {
                  console.log("[App] Healing missing email or uid on existing user document", currentUser.uid);
                  await setDoc(docRef, {
                    uid: currentUser.uid,
                    ...(emailLower ? { email: emailLower } : {}),
                    name: existingData?.name || currentUser.displayName || "",
                  }, { merge: true });
                }
              }
            } catch (e) {
              console.warn("[App] Error during auto-link or profile healing (proceeding locally):", e);
            }
          };
          checkAndLink();
        }

        console.log("[useFirebaseSync] Setting up onSnapshot for user:", currentUser.uid);

        userUnsubscribe = onSnapshot(docRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data(); console.log("[useFirebaseSync] Received data from Firebase snapshot:", data);
            
            // Set subscription status for OnboardingPortal
            settersRef.current.setIsSubscriptionActive(isAdminEmail || (data.subscription_status !== 'blocked' && data.subscription_status === 'active' && data.nda_accepted));
            settersRef.current.setIsAuthLoading(false);

            // Note: symbol and timeframe are intentionally excluded from cloud sync to allow independent local sessions per device.
            // Note: visibleTimeframes is intentionally synced to persist radial menu layout across devices.
            if (data.visibleTimeframes) settersRef.current.setVisibleTimeframes(data.visibleTimeframes);

            if (data.controlRotateSpeed !== undefined) settersRef.current.setControlRotateSpeed(data.controlRotateSpeed);
            if (data.controlPanSpeed !== undefined) settersRef.current.setControlPanSpeed(data.controlPanSpeed);
            if (data.controlZoomSpeed !== undefined) settersRef.current.setControlZoomSpeed(data.controlZoomSpeed);
            if (data.cameraStates) {
              settersRef.current.setCameraStates(data.cameraStates);
              localStorage.setItem('cameraStates', JSON.stringify(data.cameraStates));
            }
            if (data.show3D !== undefined) settersRef.current.setShow3D(data.show3D);
            if (data.backwallStyle) settersRef.current.setBackwallStyle(data.backwallStyle);
            if (data.showShadowFloor !== undefined) settersRef.current.setShowShadowFloor(data.showShadowFloor);
            if (data.showShadowSide !== undefined) settersRef.current.setShowShadowSide(data.showShadowSide);
            if (data.showMidLineWall !== undefined) settersRef.current.setShowMidLineWall(data.showMidLineWall);
            if (data.midLineWallColor) settersRef.current.setMidLineWallColor(data.midLineWallColor);
            if (data.midLineWallOpacity !== undefined) settersRef.current.setMidLineWallOpacity(data.midLineWallOpacity);
            if (data.colorUp) settersRef.current.setColorUp(data.colorUp);
            if (data.colorDown) settersRef.current.setColorDown(data.colorDown);
            if (data.colorIntensity) settersRef.current.setColorIntensity(data.colorIntensity);
            if (data.colorContrast) settersRef.current.setColorContrast(data.colorContrast);
            if (data.momentumSensitivity) settersRef.current.setMomentumSensitivity(data.momentumSensitivity);
            if (data.useRealData !== undefined) settersRef.current.setUseRealData(data.useRealData);
            if (data.showSMA10 !== undefined) settersRef.current.setShowSMA10(data.showSMA10);
            if (data.showSMA20 !== undefined) settersRef.current.setShowSMA20(data.showSMA20);
            if (data.showSMA50 !== undefined) settersRef.current.setShowSMA50(data.showSMA50);
            if (data.showGrid !== undefined) settersRef.current.setShowGrid(data.showGrid);
            if (data.showConnectors !== undefined) settersRef.current.setShowConnectors(data.showConnectors);
            if (data.showPeriodSeparators !== undefined) settersRef.current.setShowPeriodSeparators(data.showPeriodSeparators);
            
            // Missing visual settings setters
            if (data.gridColor) settersRef.current.setGridColor(data.gridColor);
            if (data.gridOpacity !== undefined) settersRef.current.setGridOpacity(data.gridOpacity);
            if (data.gridThickness !== undefined) settersRef.current.setGridThickness(data.gridThickness);
            if (data.connectorsOpacity !== undefined) settersRef.current.setConnectorsOpacity(data.connectorsOpacity);
            if (data.edgesColor) settersRef.current.setEdgesColor(data.edgesColor);
            if (data.edgesOpacity !== undefined) settersRef.current.setEdgesOpacity(data.edgesOpacity);
            if (data.buyersColor) settersRef.current.setBuyersColor(data.buyersColor);
            if (data.sellersColor) settersRef.current.setSellersColor(data.sellersColor);
            if (data.floorLabelsOpacity !== undefined) settersRef.current.setFloorLabelsOpacity(data.floorLabelsOpacity);
            if (data.sideLabelsOpacity !== undefined) settersRef.current.setSideLabelsOpacity(data.sideLabelsOpacity);
            if (data.titleSize !== undefined) settersRef.current.setTitleSize(data.titleSize);
            if (data.floorLabelsSize !== undefined) settersRef.current.setFloorLabelsSize(data.floorLabelsSize);
            if (data.sideLabelsSize !== undefined) settersRef.current.setSideLabelsSize(data.sideLabelsSize);
            if (data.floorLabelsSpread !== undefined) settersRef.current.setFloorLabelsSpread(data.floorLabelsSpread);
            if (data.sideLabelsSpread !== undefined) settersRef.current.setSideLabelsSpread(data.sideLabelsSpread);
            if (data.timeLabelsSize !== undefined) settersRef.current.setTimeLabelsSize(data.timeLabelsSize);
            if (data.periodLabelsSize !== undefined) settersRef.current.setPeriodLabelsSize(data.periodLabelsSize);
            if (data.periodLabelsOpacity !== undefined) settersRef.current.setPeriodLabelsOpacity(data.periodLabelsOpacity);
            if (data.timeLabelsOpacity !== undefined) settersRef.current.setTimeLabelsOpacity(data.timeLabelsOpacity);
            if (data.momentumLabelsSize !== undefined) settersRef.current.setMomentumLabelsSize(data.momentumLabelsSize);
            if (data.momentumLabelsOpacity !== undefined) settersRef.current.setMomentumLabelsOpacity(data.momentumLabelsOpacity);
            if (data.priceLabelsSize !== undefined) settersRef.current.setPriceLabelsSize(data.priceLabelsSize);
            if (data.priceLabelsOpacity !== undefined) settersRef.current.setPriceLabelsOpacity(data.priceLabelsOpacity);
            if (data.periodColor) settersRef.current.setPeriodColor(data.periodColor);
            if (data.periodOpacity !== undefined) settersRef.current.setPeriodOpacity(data.periodOpacity);
            if (data.periodLineStyle !== undefined) settersRef.current.setPeriodLineStyle(data.periodLineStyle);
            if (data.periodLineWidth !== undefined) settersRef.current.setPeriodLineWidth(data.periodLineWidth);
            if (data.showPeriodLabels !== undefined) settersRef.current.setShowPeriodLabels(data.showPeriodLabels);
            if (data.curveTension !== undefined) settersRef.current.setCurveTension(data.curveTension);
            if (data.backdropBlur !== undefined) settersRef.current.setBackdropBlur(data.backdropBlur);
            if (data.radialMenuColor !== undefined) settersRef.current.setRadialMenuColor(data.radialMenuColor);
            if (data.buttonBaseColor !== undefined) settersRef.current.setButtonBaseColor(data.buttonBaseColor);
            
            if (data.floorChartOpacity !== undefined) settersRef.current.setFloorChartOpacity(data.floorChartOpacity);
            if (data.sideChartOpacity !== undefined) settersRef.current.setSideChartOpacity(data.sideChartOpacity);
            if (data.backChartOpacity !== undefined) settersRef.current.setBackChartOpacity(data.backChartOpacity);
            if (data.floorSmaOpacity !== undefined) settersRef.current.setFloorSmaOpacity(data.floorSmaOpacity);
            if (data.sideSmaOpacity !== undefined) settersRef.current.setSideSmaOpacity(data.sideSmaOpacity);
            if (data.backSmaOpacity !== undefined) settersRef.current.setBackSmaOpacity(data.backSmaOpacity);
            if (data.main3DOpacity !== undefined) settersRef.current.setMain3DOpacity(data.main3DOpacity);
            if (data.sma103DOpacity !== undefined) settersRef.current.setSma103DOpacity(data.sma103DOpacity);
            if (data.sma203DOpacity !== undefined) settersRef.current.setSma203DOpacity(data.sma203DOpacity);
            if (data.sma503DOpacity !== undefined) settersRef.current.setSma503DOpacity(data.sma503DOpacity);
            
            if (data.floorChartLineWidth !== undefined) settersRef.current.setFloorChartLineWidth(data.floorChartLineWidth);
            if (data.sideChartLineWidth !== undefined) settersRef.current.setSideChartLineWidth(data.sideChartLineWidth);
            if (data.floorSmaLineWidth !== undefined) settersRef.current.setFloorSmaLineWidth(data.floorSmaLineWidth);
            if (data.sideSmaLineWidth !== undefined) settersRef.current.setSideSmaLineWidth(data.sideSmaLineWidth);
            if (data.backSmaLineWidth !== undefined) settersRef.current.setBackSmaLineWidth(data.backSmaLineWidth);
            if (data.lineWidth3D !== undefined) settersRef.current.setLineWidth3D(data.lineWidth3D);

            if (data.sma10Period !== undefined) settersRef.current.setSma10Period(data.sma10Period);
            if (data.sma20Period !== undefined) settersRef.current.setSma20Period(data.sma20Period);
            if (data.sma50Period !== undefined) settersRef.current.setSma50Period(data.sma50Period);
            if (data.candleGraphDistance3D !== undefined) settersRef.current.setCandleGraphDistance3D(data.candleGraphDistance3D);
            if (data.floorDistance3D !== undefined) settersRef.current.setFloorDistance3D(data.floorDistance3D);
            if (data.sideGraphDistance3D !== undefined) settersRef.current.setSideGraphDistance3D(data.sideGraphDistance3D);
            if (data.width3D !== undefined) settersRef.current.setWidth3D(data.width3D);
            if (data.heightScale3D !== undefined) settersRef.current.setHeightScale3D(data.heightScale3D);
            if (data.zLimit3D !== undefined) settersRef.current.setZLimit3D(data.zLimit3D);
            if (data.lockedSliders !== undefined) settersRef.current.setLockedSliders(data.lockedSliders);
            if (data.connectorsThickness !== undefined) settersRef.current.setConnectorsThickness(data.connectorsThickness);
            if (data.favorites !== undefined && Array.isArray(data.favorites)) {
              settersRef.current.setFavorites(data.favorites);
              localStorage.setItem('favorites', JSON.stringify(data.favorites));
            }
            
          } else {
            settersRef.current.setIsSubscriptionActive(isAdminEmail);
            settersRef.current.setIsAuthLoading(false);
          }
        }, (error) => {
          handleFirestoreError(error, OperationType.GET, `users/${currentUser.uid}`);
          settersRef.current.setIsSubscriptionActive(isAdminEmail);
          settersRef.current.setIsAuthLoading(false);
        });
      } else {
        // Sem usuário: NÃO marcar isAuthLoading=false aqui. Quem decide isso é o
        // onAuthStateChanged (useAuth), depois que o Firebase terminar de
        // restaurar a sessão. Marcar antes disparava o login automático por
        // licença antes de o Firebase reconhecer a conta (corrida no carregamento).
        settersRef.current.setIsSubscriptionActive(false);
      }

    return () => {
      if (userUnsubscribe) {
        console.log("[useFirebaseSync] Cleaning up onSnapshot");
        userUnsubscribe();
      }
    };
  }, [user?.uid]); // Only re-run if the user UID changes
}
